import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { Mail, Search } from "lucide-react";

const FlagFR = ({ active }) => (
  <svg width="18" height="13" viewBox="0 0 22 16" className={`block rounded-sm border ${active ? "border-white" : "border-[#374151]"}`} aria-hidden>
    <rect width="7.33" height="16" fill="#002395" />
    <rect x="7.33" width="7.33" height="16" fill="#fff" />
    <rect x="14.66" width="7.33" height="16" fill="#ED2939" />
  </svg>
);

const FlagGB = ({ active }) => (
  <svg width="18" height="13" viewBox="0 0 60 30" className={`block rounded-sm border ${active ? "border-white" : "border-[#374151]"}`} aria-hidden>
    <clipPath id="t"><path d="M30,15h30v15zv15h-30zh-30v-15zv-15h30z" /></clipPath>
    <path d="M0,0v30h60v-30z" fill="#012169" />
    <path d="M0,0 60,30M60,0 0,30" stroke="#fff" strokeWidth="6" />
    <path d="M0,0 60,30M60,0 0,30" clipPath="url(#t)" stroke="#C8102E" strokeWidth="4" />
    <path d="M30,0v30M0,15h60" stroke="#fff" strokeWidth="10" />
    <path d="M30,0v30M0,15h60" stroke="#C8102E" strokeWidth="6" />
  </svg>
);

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-ops-signal/artifacts/ocf5prlb_dataleo_large.png";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { to: "/", label: t.nav.home, end: true, id: "nav-home" },
    { to: "/news", label: t.nav.news, id: "nav-news" },
    { to: "/jobs", label: t.nav.jobs, id: "nav-jobs" },
    { to: "/alerts", label: t.nav.alerts, id: "nav-alerts" },
    { to: "/insights", label: t.nav.insights, id: "nav-insights" },
    { to: "/tutorials", label: t.nav.tutorials, id: "nav-tutorials" },
    { to: "/ecosystem", label: t.nav.ecosystem, id: "nav-ecosystem" },
    { to: "/about", label: t.nav.about, id: "nav-about" },
  ];

  const handleSubscribe = () => {
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth" }), 300);
    } else {
      document.getElementById("newsletter")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-[#111827] text-white border-b border-[#1F2937]">
      <div className="dl-container flex items-center justify-between h-14 gap-2 sm:gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group shrink min-w-0">
          <img
            src={LOGO_URL}
            alt="Dataleo"
            className="h-6 sm:h-8 w-auto bg-black rounded-sm p-0.5 shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="leading-tight hidden md:block">
            <div className="text-[12px] font-semibold tracking-tight text-white whitespace-nowrap">
              Radar <span className="text-[#00A878]">/</span> <span className="font-mono-dl text-[10px] tracking-[0.18em] uppercase text-[#9ca3af]">Supply Chain AI</span>
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <button
            data-testid="search-icon-btn"
            onClick={() => navigate("/search")}
            aria-label="Search"
            className="grid place-items-center w-8 h-8 sm:w-9 sm:h-9 text-[#9ca3af] hover:text-white hover:bg-[#1F2937] transition-colors"
          >
            <Search size={16} />
          </button>

          <div data-testid="lang-switcher" className="flex items-center gap-0.5 sm:gap-1.5">
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              aria-label="Français"
              className={`p-1 transition-opacity ${lang === "fr" ? "opacity-100" : "opacity-50 hover:opacity-90"}`}
            >
              <FlagFR active={lang === "fr"} />
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              aria-label="English"
              className={`p-1 transition-opacity ${lang === "en" ? "opacity-100" : "opacity-50 hover:opacity-90"}`}
            >
              <FlagGB active={lang === "en"} />
            </button>
          </div>

          <button
            data-testid="subscribe-btn"
            onClick={handleSubscribe}
            aria-label={lang === "fr" ? "S'abonner" : "Subscribe"}
            className="inline-flex items-center justify-center gap-1.5 bg-[#FFD400] text-[#111827] px-2.5 sm:px-4 h-8 sm:h-9 text-[13px] font-bold tracking-tight hover:bg-[#facc15] transition-colors"
          >
            <Mail size={14} />
            <span className="hidden sm:inline">{lang === "fr" ? "S'abonner" : "Subscribe"}</span>
          </button>
        </div>
      </div>

      <div className="bg-white border-t border-[#E5E7EB]">
        <div className="dl-container flex items-center gap-6 h-11 overflow-x-auto" data-testid="primary-nav">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.id}
              className={({ isActive }) =>
                `text-[13px] font-medium whitespace-nowrap transition-colors ${
                  isActive ? "text-[#111827] border-b-2 border-[#00A878] -mb-[1px] pb-[8px]" : "text-[#4B5563] hover:text-[#111827]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;
